import React from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  LinearProgress
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';

interface ServicePenetrationData {
  multiServiceClients: {
    clientId: string;
    clientName: string;
    serviceCount: number;
    serviceTypes: string[];
    totalRevenue: number;
  }[];
  crossSellOpportunities: {
    clientId: string;
    clientName: string;
    currentServices: string[];
    missingServices: string[];
  }[];
  serviceAdoptionStats: {
    serviceType: string;
    clientCount: number;
    penetrationRate: number;
  }[];
}

interface ServicePenetrationAnalysisProps {
  data: ServicePenetrationData;
  isLoading: boolean;
}

const ServicePenetrationAnalysis: React.FC<ServicePenetrationAnalysisProps> = ({ data, isLoading }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2, color: '#fff' }}>Loading service penetration data...</Typography>
      </Paper>
    );
  }

  if (!data) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography sx={{ color: '#fff' }}>No service penetration data available</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <TrendingUpIcon sx={{ color: '#fff', mr: 1 }} />
        <Typography variant="h6" sx={{ color: '#fff' }}>
          Service Penetration Analysis
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Multi-Service Clients */}
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ color: '#4caf50', mr: 1 }} />
                <Typography variant="h6" sx={{ color: '#fff', fontSize: '1rem' }}>
                  Top Multi-Service Clients
                </Typography>
              </Box>
              
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {data.multiServiceClients?.slice(0, 8).map((client, index) => (
                  <ListItem key={client.clientId} sx={{ px: 0, py: 1 }}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.875rem' }}>
                          {client.clientName}
                        </Typography>
                        <Chip
                          label={`${client.serviceCount} services`}
                          size="small"
                          color="success"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                        {client.serviceTypes.slice(0, 4).map((service, idx) => (
                          <Chip
                            key={idx}
                            label={service}
                            size="small"
                            variant="outlined"
                            sx={{
                              color: '#fff',
                              borderColor: '#fff',
                              fontSize: '0.7rem'
                            }}
                          />
                        ))}
                        {client.serviceTypes.length > 4 && (
                          <Chip
                            label={`+${client.serviceTypes.length - 4}`}
                            size="small"
                            variant="outlined"
                            sx={{
                              color: '#fff',
                              borderColor: '#fff',
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                      </Box>
                      <Typography sx={{ color: '#bbb', fontSize: '0.75rem' }}>
                        Revenue: {formatCurrency(client.totalRevenue)}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Cross-Sell Opportunities */}
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon sx={{ color: '#ff9800', mr: 1 }} />
                <Typography variant="h6" sx={{ color: '#fff', fontSize: '1rem' }}>
                  Cross-Sell Opportunities
                </Typography>
              </Box>
              
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {data.crossSellOpportunities?.slice(0, 8).map((opportunity, index) => (
                  <ListItem key={opportunity.clientId} sx={{ px: 0, py: 1 }}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.875rem' }}>
                          {opportunity.clientName}
                        </Typography>
                        <Chip
                          label={`${opportunity.missingServices.length} opportunities`}
                          size="small"
                          color="warning"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Box>
                      <Typography sx={{ color: '#e0e0e0', fontSize: '0.75rem', mb: 0.5 }}>
                        Current: {opportunity.currentServices.join(', ')}
                      </Typography>
                      <Typography sx={{ color: '#ffb74d', fontSize: '0.75rem', mb: 0.5, fontWeight: 500 }}>
                        Potential: {opportunity.missingServices.join(', ')}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Service Adoption Stats */}
        <Grid item xs={12}>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontSize: '1rem' }}>
                Service Adoption Rates
              </Typography>
              
              <Grid container spacing={2}>
                {data.serviceAdoptionStats?.map((stat, index) => (
                  <Grid item xs={12} sm={6} md={4} key={stat.serviceType}>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.875rem' }}>
                          {stat.serviceType}
                        </Typography>
                        <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
                          {stat.clientCount} clients
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={stat.penetrationRate}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: stat.penetrationRate > 70 ? '#4caf50' : 
                                           stat.penetrationRate > 40 ? '#ff9800' : '#f44336',
                            borderRadius: 4
                          }
                        }}
                      />
                      <Typography sx={{ color: '#bbb', fontSize: '0.75rem', mt: 0.5 }}>
                        {stat.penetrationRate.toFixed(1)}% penetration
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
};

export { ServicePenetrationAnalysis };
